import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { useLocation } from 'react-router-dom';

import Card from 'components/Account/Common/Card/Card';
import ErrorMessage from 'components/Common/Formik/ErrorMessage/ErrorMessage';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import RadioFieldCustom from 'components/Common/Formik/RadioFieldCustom/RadioFieldCustom';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import validationSchema from 'schemas/ManageSubscriptionsErrors';
import Button from 'components/Common/Button/Button';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { useCustomer } from 'hooks/useCustomer';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useFlashMessage from 'hooks/useFlashMessage';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { GTM_PAGE_ACCOUNT } from 'settings/gtm/gtmPageView';
import { GTM_FORM_MANAGE_SUBSCRIPTION } from 'settings/gtm/gtmForm';

import styles from './ManageSubscription.module.scss';

function ManageSubscription(): JSX.Element {
  const { setFlashMessage } = useFlashMessage();
  const { t } = useTranslation(['newslettersSubPage']);
  const { customer, refreshCustomer, smsOptin } = useCustomer();
  const { isUk } = useCurrentCountry();
  const { state } = useLocation();
  const mailOption = state?.mailOption || customer?.mailOption;
  const partOption = customer?.partOption;
  const [initialValues, setInitialValues] = useState({
    mailOption: mailOption ? 'O' : 'N',
    smsOption: null === smsOptin ? null : smsOptin ? 'O' : 'N',
    partOption: partOption ? 'O' : 'N',
    cellPhone: customer?.cellPhone || '',
  });

  const handleResetForm = (
    actions: FormikHelpers<typeof initialValues>,
    values: typeof initialValues
  ) => {
    setTimeout(() => {
      setInitialValues({
        mailOption: values.mailOption,
        smsOption: values.smsOption,
        partOption: values.partOption,
        cellPhone: values.cellPhone,
      });
      actions.resetForm();
    }, 1000);
  };

  const radioExtraStyle: ExtraStyleType = {
    container: styles.radioContainer,
    fieldContainer: styles.radioFieldContainer,
    label: styles.radioLabel,
  };

  const extraStyleTextField: ExtraStyleType = {
    container: styles.textFieldContainer,
    fieldContainer: styles.fieldContainerTextField,
    label: styles.textFieldLabel,
  };

  useEffect(() => {
    if (state && state.mailOption) {
      delete state['mailOption'];
      history.replaceState(state, document.title);
    }
  }, []);

  const onSubmit = (
    values: typeof initialValues,
    actions: FormikHelpers<typeof initialValues>
  ) => {
    if (customer) {
      const { smsOption, partOption, mailOption, cellPhone } = values;
      const cleanedCellPhone = replacePhoneNumberWithoutSpace(cellPhone || '');
      const body = {
        email: customer.email,
        mailOption,
        smsOption: cleanedCellPhone ? smsOption : null,
        partOption,
        cellPhone: cleanedCellPhone,
        evt: 'newsletter',
      };

      if (cellPhone === '') {
        Reflect.deleteProperty(body, 'cellPhone');
      }

      fetcherWithBody(apiUrls.getCustomerUpdate(), body).then(() => {
        setFlashMessage(t('LABEL_NEWSLETTER_REGISTERED'));
        refreshCustomer();
        handleResetForm(actions, values);
      });
    }
  };

  const newsOption = [
    {
      label: t(`LABEL_YES`, { ns: 'translation' }),
      value: 'O',
    },
    {
      label: t(`LABEL_NO`, { ns: 'translation' }),
      value: 'N',
    },
  ];

  const fields = [
    {
      labelTitle: t('LABEL_NEWSLETTER_AFIBEL_EMAIL'),
      name: 'mailOption',
      required: true,
    },
    {
      labelTitle: t('LABEL_NEWSLETTER_AFIBEL_SMS'),
      name: 'smsOption',
    },
  ];

  return (
    <Card classes={{ container: styles.container }}>
      <div className={styles.title}>
        <label>{t('LABEL_NEWSLETTER_MANAGE_TITLE')}</label>
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <FontAwesomeIcon icon={faChevronRight} />
          <label>{t('LABEL_NEWSLETTER_SUBTITLE')}</label>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(isUk, t)}
          enableReinitialize={true}
          onSubmit={(values, actions) => onSubmit(values, actions)}
        >
          {({ values }) => (
            <Form className="form" noValidate>
              <FormikErrorsGtmLogger
                page_type={GTM_PAGE_ACCOUNT}
                form_type={GTM_FORM_MANAGE_SUBSCRIPTION}
              />
              <div className={styles.containerNews}>
                <label className={styles.span}>
                  {t('LABEL_NEWSLETTER_WISH_AFIBEL')}
                </label>

                <div className={styles.fields}>
                  {fields.map(field => {
                    const { labelTitle, name, required } = field;

                    return (
                      <div
                        className={`${
                          name === 'smsOption' ? styles.fieldSMS : styles.field
                        }`}
                        key={name}
                      >
                        <RadioFieldCustom
                          label={labelTitle}
                          name={name}
                          options={newsOption}
                          extraStyle={radioExtraStyle}
                          required={required}
                        />
                        {name === 'smsOption' &&
                          !customer?.cellPhone &&
                          values[name] === 'O' && (
                            <div>
                              <TextFieldCustom
                                label={t('LABEL_CELL_PHONE')}
                                name="cellPhone"
                                type="tel"
                                validation="number"
                                required
                                extraStyle={extraStyleTextField}
                                isErrorVisible={false}
                              />
                              <ErrorMessage
                                name="cellPhone"
                                additionalClass={styles.messageError}
                              />
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
                <div className={styles.dotted} />
                <label className={styles.span}>
                  {t('LABEL_NEWSLETTER_WISH_PARTNER')}
                </label>
                <div className={styles.fields}>
                  <div className={styles.field}>
                    <RadioFieldCustom
                      label={t('LABEL_NEWSLETTER_PARTNER_EMAIL')}
                      name="partOption"
                      options={newsOption}
                      extraStyle={radioExtraStyle}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className={styles.actionToolbar}>
                <div className={styles.containerButton}>
                  <Button>{t('LABEL_NEWSLETTER_CONFIRM')}</Button>
                </div>
                <div className={styles.requiredField}>
                  <label>
                    <span>*</span>
                    {t('LABEL_REQUIRED_INFORMATION')}
                  </label>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Card>
  );
}

export default ManageSubscription;
